<template>
  <GcBaseLayout
    :title="leftGridConfig.title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Grower Syndicate Splits'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="'Grower Syndicate Splits'"
        :secondaryLabel="undefined"
      />
    </template>
    <div class="split-view-template css-v2 split-view grower-syndicate-splits">
      <GcSplitView
        :showLeftContainer="true"
        :showRightContainer="showRightPanel"
      >
        <template #leftContent>
          <div class="css-v2 split-view-list">
            <GcDataGrid
              :grouping="true"
              :export="true"
              :summary="true"
              :dataSourceConfig="leftGridConfig.gridData.gridOptions.dataSource"
              :columns="leftGridConfig.gridData.columns"
              :gridToolbar="leftGridConfig.gridData.toolbar"
              :padColumn="true"
              :id="'grower-syndicate-splits-left-grid'"
              :rowClick="onLeftRowClick"
              :exportFileName="leftGridConfig.exportFileName"
              :heightPadding="20"
              :paging="false"
              ref="leftGrid"
              :filterHeightAdjustment="-10"
            />
          </div>
        </template>

        <template #rightContent>
          <div class="right-panel-container" v-if="showRightPanel">
            <GcFormHeaderToolbar
              :toolbarOptions="rightToolbarOptions"
              :dataModel="toolbarRecord"
              :baseView="baseView"
              :backRoute="leftGridConfig.pageRoute"
              :itemSaveCallBack="onSaveClicked"
            />

            <div class="selected-record-info">
              <GcForm
                :formOptions="{ gcLabelLocation: 'left' }"
                :formItems="identityFormItems"
                :isNormal="true"
                :data-model="selectedRecord"
                :formMode="baseView.mode"
                :formLocation="'normal'"
                :formContentReady="formContentReady"
                :formHeight="baseView.mode === 'create' ? 110 : undefined"
              />
            </div>

            <div class="view-card responsive-paddings split-view-right-grid">
              <DxDataGrid
                :data-source="splitRows"
                key-expr="RowKey"
                :show-borders="false"
                :repaint-changes-only="true"
                :column-auto-width="true"
                :no-data-text="
                  isEditing
                    ? 'No Grower rows yet - click Add Grower above.'
                    : 'No split recorded for this Syndicate+Season yet.'
                "
                @init-new-row="onInitNewRow"
                :columnChooser="{
                  enabled: false,
                }"
                @toolbar-preparing="onGridToolbarPreparing"
                :height="getHeight"
                @row-removed="onRowRemoved"
                @content-ready="onGridContentReady"
                :ref="'rightGrid'"
                :columns="getEditableColumns"
                @editor-preparing="onEditorPreparing"
              >
                <DxLoadPanel :show-indicator="false" :enabled="false" />
                <DxPaging :page-size="50" :enabled="false" />
                <DxPager :visible="false" />
                <DxEditing
                  mode="cell"
                  :allow-updating="isEditing"
                  :allow-adding="isEditing"
                  :allow-deleting="true"
                  :use-icons="true"
                  :select-text-on-edit-start="true"
                />

                <template #growerCellTemplate="{ data }">
                  <span>{{ growerDisplayText(data.data.GrowerCode) }}</span>
                </template>

                <DxSummary>
                  <DxTotalItem
                    name="SplitPcnt"
                    column="SplitPcnt"
                    summary-type="sum"
                    value-format="##,###,###,###,##0.0000;(##,###,###,###,##0.0000)"
                    display-format="Total: {0} %"
                  />
                </DxSummary>
              </DxDataGrid>

              <!-- §6/AC footer: Row Count + running Total, good/bad colour state at
                   exactly 100 - persistent, not just a Save-time toast. -->
              <div class="split-grid-footer">
                <span>Row Count: {{ filledRowCount }}</span>
                <span>
                  Total Split:
                  <span
                    class="total-value"
                    :class="totalIsValid ? 'good' : 'bad'"
                    >{{ totalDisplay }}</span
                  >
                </span>
              </div>
            </div>
          </div>
        </template>
      </GcSplitView>
    </div>
  </GcBaseLayout>
</template>

<script>
/*
Description
    grower-syndicate-splits.vue - Settings > Intake > Grower Syndicate Splits
    (Project 2116, Almondco Intake Masters Migration). List + filter panel (#76547),
    the Review/Edit detail panel with running Total and self-reference check
    (#76577), and the + ADD NEW Season+Syndicate picker (#76580).

    #76580: the picker is a real dxSelectBox *pair* (Season, then Syndicate
    filtered to that season's own unsplit combinations), not a single combined
    value like the sibling grower-pool-splits.vue's own Depot Docket picker -
    technical-design.md §6/AD-04, since a Syndicate can independently have up to
    3 unsplit season slots at once (§4). The Syndicate picker's own dataSource is
    a single, stable DevExtreme DataSource instance (created once, see created())
    re-filtered/reloaded in place on Season change, rather than rebuilt on every
    identityFormItems recomputation. Season option list mirrors
    console-season-year.vue's own setDefaultSeasonYear (find the AlmondcoIntakeSeason
    row whose date range contains today), reuse-before-create rather than a
    second "current fiscal year" definition (AD-03's own Build-time note).

    Also introduces the create-vs-update save-permission ternary technical-
    design.md §6 originally specified (project-wide PO ruling 2026-08-25) now
    that a real create-mode entry point exists - previously hardcoded to
    'create' in both modes, matching the sibling's own pre-Add-New interim
    state (#76577).

    #76577 deliberately does NOT mirror the sibling grower-pool-splits.vue's own
    current getEditableColumns 1:1: that file's GrowerCode column carries no
    validationRules at all (growerValidationRules/splitPcntValidationRules are
    defined there but unused - dead code left over from its own manual rebuild off
    static DxColumn definitions), and its custom running-Total footer div is
    commented out, leaving only a plain, uncoloured native DxSummary total. Per
    this story's own AC ("Apply on a row blocks... a Grower code equal to the
    header Syndicate's own code, or a duplicate Grower" and "good/bad visual state
    at exactly 100"), both are wired up properly here instead - flagging the
    discrepancy rather than silently copying it, not touching the sibling file.

    Follows the sibling grower-pool-splits.vue's own rebuilt shape (GcBaseLayout +
    GcSplitView + GcDataGrid, bypassing SplitViewGridTemplate entirely - developer
    direction, 2026-08-26) rather than technical-design.md §6's originally-specified
    SplitViewGridTemplate, for consistency with how the sibling actually ended up
    being built.

    Route identity - RecordId, not the natural SeasonYear+SyndicateCode composite
    key (developer direction, #76547): technical-design.md's own AD-04 specified two
    route params (:syndicateCode/:seasonYear), but RouteMixin
    (src/_mixins/route.mixin.js) only ever parses a single :id URL segment - every
    other List+Detail screen in this codebase relies on that same mixin. Rather than
    extend a mixin shared by the whole app, AlmondcoGrowerSyndicateSplitSummary.sql
    now exposes RecordId, a deterministic GUID computed from
    SyndicateCode+SeasonYear - the same "expose a RecordId for routing" approach
    already used by bin-comments for its own Code+Site composite key (see
    router/settings/intake.js's bin-comments comment). SeasonYear/SyndicateCode
    remain on the record for display/identity purposes; RecordId is purely the
    routable key.

    Filter panel field resolution (§6/§10):
      - Season    -> screenField "AlmondcoIntakeSeason", filterDataField
                     "SeasonYear", filterDefaultValue "CurrentFiscalYear" - reused
                     exactly as the sibling grower-pool-splits.vue's own Season
                     filter column, no override needed.
      - Syndicate -> screenField "Grower" (existing global row, reused/overridden
                     the same way the sibling overrode it for its own "Grower Pool"
                     column - see AlmondcoGrowerSyndicateSearch.cs's own history).
                     Filter option list sourced from AlmondcoGrowerSyndicateSearch
                     (#76547), NOT AlmondcoGrowerSyndicateSplitSummary - the summary
                     view is graded per (Syndicate, Season), so a Syndicate with
                     splits in more than one season would show its name duplicated
                     in the dropdown (BR-01, same bug class as #75930 on the
                     sibling's own Grower Pool filter).

History
    31Aug2026 (SD)
        - #76547: 2116 - UI - Grower Syndicate Splits List and filter panel
        - #76577: 2116 - UI - Grower Syndicate Split detail panel (Review/Edit grid,
          running Total, self-reference check)
        - #76580: 2116 - UI - Add New Season+Syndicate picker (unsplit combinations)
    01Sep2026 (SD)
        - #77235: Bug fix - inline field-level validationRules on GrowerCode/
          SplitPcnt (Split %>0, self-reference, duplicate Grower) trapped the
          user with no escape (blocked cell, dead delete icon) once triggered -
          removed from the grid columns entirely; all three now enforced once,
          at Save time, in validateBeforeSave (see its own comment)
    10Sep2026 (AE)
        - Bug fix (AD-05, technical-design.md): the List's own dataSource was
          still pointed at AlmondcoGrowerSyndicateSplitSummary - the AD-03
          candidate cross join meant for the Add-New picker only - so it showed
          mostly never-actually-split "phantom" GrowerCount=0 rows and hid any
          real split recorded for a Season outside AlmondcoIntakeSeason's own
          row range (e.g. pre-2019). Switched to the new, real-data-only
          AlmondcoGrowerSyndicateSplitListView. syndicateOptionsDataSource
          (Add-New picker, in created() below) is deliberately unchanged - it
          still needs the Summary view's candidate/unsplit-combination shape.

(c) Copyright Graycorp Pty Ltd (ABN 12 082 807 761). All Rights Reserved.
*/
import { FeatureStorage } from "@/_services/storage";
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";
import { store } from "@/_store";
import { AlmondcoGrowerSyndicateSplitService } from "@/_services/api/settings/purchasing/almondco-grower-syndicate-split.service";
import { DxDataGrid, DxSummary, DxTotalItem } from "devextreme-vue/data-grid";

import { backwardTabbing, forwardTabbing } from "../../../_helpers/ui/keyboard";

export default {
  mixins: [RouteMixin, FormMixin],
  components: { DxDataGrid, DxSummary, DxTotalItem },
  data() {
    return {
      ColumnWidth,

      // The selected row's identity fields (Season/SyndicateCode/SyndicateName),
      // from AlmondcoGrowerSyndicateSplitSummary - read-only, resolved by RecordId.
      selectedRecord: {},
      toolbarRecord: {},

      // Local, mutable array bound directly to DxDataGrid's data-source -
      // Add/Remove/edit all operate on this array in place via DxEditing's native
      // allow-adding/allow-deleting/allow-updating (matches the sibling's own
      // approach for this same grid shape).
      splitRows: [],
      nextRowKey: 1,
      // Tracks whether loadSelectedRecord's own fetch for the CURRENT
      // baseView.id has actually landed - guards the mode watcher's own
      // "add a blank row on Edit" branch against a race on a direct/refreshed
      // load straight into Edit mode (see loadSelectedRecord/the mode watcher).
      splitRowsLoaded: false,

      // AlmondcoGrowerRecipientSearch results (AD-01, reused not rebuilt), loaded
      // once - the Grower picker option list is Syndicate+Season-independent.
      growerOptions: [],

      // #76580 (+ ADD NEW): Season/Syndicate picker state - a real dxSelectBox
      // pair (technical-design.md §6), not a single combined value like the
      // sibling's own Depot Docket picker, since a Syndicate can independently
      // have up to 3 unsplit season slots at once (§4/AD-04).
      createSeasonYear: null,
      createSyndicateCode: null,
      seasonOptions: [],
      // A real DevExtreme DataSource instance, created once (see created()) and
      // re-filtered/reloaded in place (onCreateSeasonChanged) rather than
      // rebuilt - keeps the Syndicate dxSelectBox's own binding stable across
      // identityFormItems recomputation.
      syndicateOptionsDataSource: null,

      headerFormInstance: undefined,
      rightGridHeight: "auto",

      leftGridConfig: {
        title: "Grower Syndicate Splits",
        pageRoute: "/grower-syndicate-splits",
        exportFileName: "Grower Syndicate Splits List",

        gridData: {
          gridOptions: {
            dataSource: {
              // AD-05: the List shows real, saved splits only -
              // AlmondcoGrowerSyndicateSplitListView (GrowerCount always > 0,
              // no AlmondcoIntakeSeason cross join). Do NOT point this back at
              // AlmondcoGrowerSyndicateSplitSummary - that view's candidate
              // cross join is the Add-New picker's own data source
              // (syndicateOptionsDataSource below), not the List's.
              endpoint: "AlmondcoGrowerSyndicateSplitListView",
              serverPaging: true,
              allowGrouping: false,
            },
          },

          toolbar: {
            search: true,
            columnChooser: true,
            export: false,
            openFilter: true,
            expand: false,
            customButtons: [
              {
                icon: "plus",
                label: "Add New",
                clickAction: () => {
                  this.addNewGrowerSyndicateSplit();
                },
                buttonLocation: "left",
                visible: FeatureStorage.checkPermission(
                  "AlmondcoGrowerSyndicateSplits",
                  "create",
                ),
              },
            ],
          },

          columns: [
            {
              // §6: Season filter default = current fiscal year. Same
              // screenField/filterDataField/filterDefaultValue recipe already live
              // on the sibling grower-pool-splits.vue - reused as-is, no override.
              screenField: "AlmondcoIntakeSeason",
              gridDataField: "SeasonYearAsString",
              filterDataField: "SeasonYear",
              label: "Season",
              filterLabel: "Season",
              filterIndex: 0,
              filterDefaultValue: "CurrentFiscalYear",
              allowHiding: false,
              width: ColumnWidth.SeasonYear.width + 30,
              minWidth: ColumnWidth.SeasonYear.minWidth + 30,
              valueExpr: "Season",
              displayExpr: "SeasonAsString",
              searchExpr: "SeasonAsString",
              forceWidth: true,
              sortIndex: 0,
              sortOrder: "desc",
            },
            {
              // Field mapping (user-stories.md Story 1): SyndicateCode /
              // SyndicateName -> "Syndicate", primary/secondary code+name per
              // common.md. Reuses the existing global "Grower" ScreenField,
              // overridden to this view's Syndicate columns - same reuse pattern
              // the sibling used for its own "Grower Pool" column.
              //
              // Filter option list sourced from AlmondcoGrowerSyndicateSearch
              // (#76547), NOT AlmondcoGrowerSyndicateSplitSummary - that view is
              // graded per (Syndicate, Season), not per Syndicate (BR-01 allows up
              // to 3 simultaneous season rows), so it would show a Syndicate's name
              // duplicated in the dropdown - same bug class as #75930.
              screenField: "Grower",
              gridDataField: "SyndicateName",
              filterDataField: "SyndicateCode",
              label: "Syndicate",
              filterLabel: "Syndicate",
              filterIndex: 1,
              valueExpr: "SyndicateCode",
              displayExpr: "SyndicateName",
              dataSourceConfig: {
                endpoint: "api/AlmondcoGrowerSyndicateSearch",
                sort: { selector: "SyndicateName", asc: true },
                select: ["SyndicateCode", "SyndicateName"],
              },
              editorTemplate: {
                primary: [{ dataField: "SyndicateName" }],
                secondary: [{ dataField: "SyndicateCode" }],
              },
              cellTemplate: "GcRecordCell",
              cellTemplateRecord: {
                primary: [{ dataField: "SyndicateName" }],
                secondary: [{ dataField: "SyndicateCode" }],
              },
              exportFields: [
                { dataField: "SyndicateCode", label: "Syndicate Code" },
                { dataField: "SyndicateName", label: "Syndicate Name" },
              ],
              allowHiding: false,
              width: ColumnWidth.CompanyName.width,
              minWidth: ColumnWidth.CompanyName.minWidth,
            },
            {
              // BR: GrowerCount = 0 is a meaningful "not yet apportioned" signal,
              // not hidden (user-stories.md Story 1). Not a filter field.
              screenField: "GrowerCount",
              dataField: "GrowerCount",
              label: "Grower Count",
              alignment: "right",
              allowHiding: false,
              width: ColumnWidth.ItemCount.width,
              minWidth: ColumnWidth.ItemCount.minWidth,
              forceWidth: true,
            },
          ],
        },
      },
    };
  },

  // Vue Router 3 only advances navigation (and mounts this component) once
  // next() is called - so awaiting the dispatch before calling it blocks the
  // route transition until CurrentFiscalYear is actually resolved (matches the
  // sibling grower-pool-splits.vue's own beforeRouteEnter).
  async beforeRouteEnter(to, from, next) {
    await store.dispatch("defaultDateSettings/setCurrentFiscalData");
    next();
  },

  computed: {
    getHeight() {
      return this.rightGridHeight;
    },
    showRightPanel() {
      return (
        (this.baseView.id !== null &&
          this.baseView.id !== undefined &&
          this.baseView.id !== "") ||
        // #76580: the create route has no :id (a Season+Syndicate isn't picked
        // yet), so the id-based check above alone would hide the panel entirely.
        this.baseView.mode === "create"
      );
    },
    isEditing() {
      // #76580: "+ ADD NEW" opens directly in Edit mode (§6, no Review step) -
      // the create picker's grid must be as editable as a real Edit session.
      return this.baseView.mode === "edit" || this.baseView.mode === "create";
    },

    // §6: no Active/Inactive toggle. save is gated on the create-vs-update
    // ternary technical-design.md §6 specifies (project-wide PO ruling
    // 2026-08-25) - not 'create' in both modes, now that a real create-mode
    // entry point exists (#76580).
    rightToolbarOptions() {
      return {
        closeSplit: true,
        edit: FeatureStorage.checkPermission("AlmondcoGrowerSyndicateSplits", "edit"),
        save: FeatureStorage.checkPermission(
          "AlmondcoGrowerSyndicateSplits",
          "create",
        ),
        audit: this.baseView.mode === "review",
      };
    },

    // Identity row (§6): Season / Syndicate, read-only, resolved from the
    // selected row - never separately re-entered. #76580: before both are
    // picked in create mode, this is instead a dxSelectBox *pair* (not a single
    // combined value like the sibling's own Depot Docket picker - technical-
    // design.md §6/AD-04, a Syndicate can independently have up to 3 unsplit
    // season slots at once). Once both are picked, selectedRecord is set
    // (onCreateSyndicateChanged) and this reactively falls through to the same
    // read-only fields the review/edit flow already uses below.
    identityFormItems() {
      if (this.baseView.mode === "create") {
        return [
          {
            itemType: "group",
            caption: "",
            colCount: 1,
            items: [
              {
                dataField: "SeasonYear",
                label: { text: "Season" },
                editorType: "dxSelectBox",
                editorOptions: {
                  dataSource: this.seasonOptions,
                  valueExpr: "Season",
                  displayExpr: "Season",
                  searchEnabled: true,
                  showClearButton: true,
                  onValueChanged: this.onCreateSeasonChanged,
                  onFocusIn: (e) => {
                    e.event.target.select();
                  },
                },
                validationRules: [
                  { type: "required", message: "Season is required" },
                ],
              },
              {
                dataField: "SyndicateCode",
                label: { text: "Syndicate" },
                editorType: "dxSelectBox",
                editorOptions: {
                  dataSource: this.syndicateOptionsDataSource,
                  valueExpr: "SyndicateCode",
                  displayExpr: (item) => {
                    return item
                      ? `${item.SyndicateCode} - ${item.SyndicateName}`
                      : "";
                  },
                  disabled: !this.createSeasonYear,
                  searchEnabled: true,
                  showClearButton: true,
                  onValueChanged: this.onCreateSyndicateChanged,
                  onKeyDown: (e) => {
                    this.onCreateSyndicateKeyDown(e);
                  },
                },
                validationRules: [
                  { type: "required", message: "Syndicate is required" },
                ],
              },
            ],
          },
        ];
      } else {
        return [
          {
            itemType: "group",
            caption: "",
            colCount: 1,
            items: [
              {
                dataField: "SeasonYear",
                label: { text: "Season" },
                editorType: "dxTextBox",
                editorOptions: { readOnly: true },
              },
              // Bug #77234: this was the only identity field shown, bound to
              // SyndicateName under a "Syndicate" label - so the panel silently
              // dropped the code entirely rather than displaying it alongside the
              // name. §6/TC-023 call for both as distinct read-only fields, in
              // Review and Edit alike (the identity row is never editable in
              // either mode) - split back into the two fields the create-mode
              // picker above already resolves onto selectedRecord.
              {
                dataField: "Syndicate",
                label: { text: "Syndicate" },
                editorType: "dxTextBox",
                editorOptions: { readOnly: true },
              },
            ],
          },
        ];
      }
    },

    // Bug 77827: Row Count must reflect actual Grower-filled rows, not the
    // always-present blank trailing row injected in Edit/Create mode (see
    // loadSelectedRecord/onRowRemoved) - same filledRows definition
    // validateBeforeSave/onSaveClicked's payload already use.
    filledRowCount() {
      return this.splitRows.filter((r) => r.GrowerCode).length;
    },

    // §6 footer.
    totalSplitPcnt() {
      return this.splitRows.reduce(
        (sum, r) => sum + (parseFloat(r.SplitPcnt) || 0),
        0,
      );
    },
    totalDisplay() {
      return this.totalSplitPcnt.toFixed(4) + " %";
    },
    // FLAG-06: strict equality to 100, matching the server - no rounding
    // tolerance. Epsilon here only absorbs JS floating-point noise.
    totalIsValid() {
      return Math.abs(this.totalSplitPcnt - 100) < 0.00001;
    },

    getEditableColumns() {
      return [
        {
          dataField: "GrowerCode",
          caption: "Grower",
          minWidth: 220,
          editorType: "dxSelectBox",
          cssClass:
            this.baseView.mode != "review"
              ? "edit-cell-textbox-field gc-editable-cell-color"
              : undefined,
          editorOptions: {
            dataSource: this.growerOptions,
            valueExpr: "GrowerCode",
            displayExpr: this.growerDisplayExpr,
            searchEnabled: true,
            searchExpr: ["GrowerCode", "GrowerName"],
            showClearButton: true,
            onKeyDown: (e, data) => {
              this.onGrowerKeyDown(e, data);
            },
          },
          cellTemplate: "growerCellTemplate",
          setCellValue: (rowData, value, currentRowData) => {
            if (currentRowData && !currentRowData.GrowerCode) {
              this.addNewRow();
            }

            rowData.GrowerCode = value;

            return rowData;
          },
        },
        {
          dataField: "SplitPcnt",
          caption: "Split %",
          minWidth: 120,
          editorType: "dxNumberBox",
          cssClass:
            this.baseView.mode != "review"
              ? "edit-cell-numberbox-field gc-editable-cell-color"
              : undefined,
          format: "##,##0.0000",
          editorOptions: {
            min: 0,
            showSpinButtons: false,
            format: "##,##0.0000",
          },
          setCellValue: (rowData, value) => {
            rowData.SplitPcnt = value;
          },
        },
        {
          visible: this.baseView.mode != "review",
          caption: "",
          dataField: "",
          type: "buttons",
          alignment: "right",
          maxWidth: 50,
          width: 50,
          buttons: ["delete"],
        },
      ];
    },
  },

  watch: {
    // Drives both the row-click flow and a direct/bookmarked URL identically -
    // baseView.id is route-derived (RouteMixin), so this fires either way.
    "baseView.id": {
      immediate: true,
      handler(recordId) {
        if (recordId) {
          this.loadSelectedRecord(recordId);
        } else {
          this.selectedRecord = {};
          this.splitRows = [];
        }

        this.setRightGridHeight();
      },
    },
    "baseView.mode": {
      immediate: true,
      handler(mode) {
        this.setRightGridHeight();

        // #76580: entering create mode fresh (first mount or navigating back to
        // /create) - reset picker state and (re)load the Season option list,
        // matching baseView.id's own watcher's "resolve everything for this
        // mode" responsibility.
        if (mode === "create") {
          this.selectedRecord = {};
          this.toolbarRecord = {};
          this.splitRows = [];
          this.createSeasonYear = null;
          this.createSyndicateCode = null;
          this.loadSeasonOptions();
        }

        // Pencil switches an existing (loaded) split into Edit mode - matches
        // the sibling grower-pool-splits.vue's own behaviour: a blank row is
        // ready to go the moment editing starts, rather than making the
        // operator click "+" first.
        //
        // Only handled here when the record is already loaded (a genuine
        // pencil click on an open Review record - baseView.id doesn't change,
        // so loadSelectedRecord never re-runs). A direct/refreshed load
        // straight into Edit mode fires this same immediate handler before
        // loadSelectedRecord's fetch has resolved - splitRowsLoaded is still
        // false then, so this is skipped and loadSelectedRecord's own
        // completion adds the row instead, once the real rows have landed
        // (otherwise the blank row got wiped out by the load overwriting
        // splitRows right after).
        if (mode === "edit" && this.splitRowsLoaded) {
          this.$nextTick(() => {
            this.addNewRow();
          });
        }
      },
    },
  },

  created() {
    this.loadGrowerOptions();
    this.syndicateOptionsDataSource =
      AlmondcoGrowerSyndicateSplitService.dataSourceConfig(
        "api/AlmondcoGrowerSyndicateSplitSummary",
        {
          filter: [["GrowerCount", "=", 0]],
          sort: { selector: "SyndicateName", asc: true },
        },
      );
  },

  methods: {
    onLeftRowClick(e) {
      if (!e.data) return;
      this.onRowClicked(e, {
        page: "grower-syndicate-splits",
        mode: "review",
        idProperty: "RecordId",
      });
    },

    backButtonFunction() {
      this.$router.push({ path: "/settings/intake" });
    },

    // §6: "+ ADD NEW" opens the detail panel directly in Edit mode via the
    // Season+Syndicate picker (#76580) - wiring the route here only.
    addNewGrowerSyndicateSplit() {
      this.$router
        .push({
          path: `${this.leftGridConfig.pageRoute}/create`,
        })
        .then(() => {
          this.$nextTick(() => {
            this.setRightGridHeight();
          });
        });
    },

    // #76580: Season picker option list - Season/StartDate/EndDate from
    // AlmondcoIntakeSeason, "current FY" resolved the same way
    // console-season-year.vue's own setDefaultSeasonYear does (the row whose
    // date range contains today), then FY-1/FY+1 alongside it (BR-01) if those
    // season rows exist yet. Defaults the picker to current FY, matching that
    // same precedent's own default-on-create behaviour.
    async loadSeasonOptions() {
      try {
        const res =
          await AlmondcoGrowerSyndicateSplitService.getIntakeSeasons();
        const rows = (res.data && res.data.value) || [];
        const today = new Date();
        const currentSeason = rows.find(
          (s) => new Date(s.StartDate) <= today && today <= new Date(s.EndDate),
        );

        if (currentSeason) {
          const relevant = [
            currentSeason.Season - 1,
            currentSeason.Season,
            currentSeason.Season + 1,
          ];
          this.seasonOptions = rows
            .filter((s) => relevant.includes(s.Season))
            .sort((a, b) => b.Season - a.Season);
          this.createSeasonYear = currentSeason.Season;
          this.onCreateSeasonChanged({ value: currentSeason.Season });
        } else {
          this.seasonOptions = rows;
        }
      } catch (err) {
        this.seasonOptions = [];
      }
    },

    // #76580: Season changed - reset the Syndicate selection and re-filter its
    // dataSource in place (a stable DataSource instance, not rebuilt - see
    // created()) to only that season's own unsplit (GrowerCount eq 0)
    // Syndicates. selectedRecord.SeasonYear is set here (not just once the
    // Syndicate is also picked) since GcForm's Season editor is bound to it via
    // dataField - same data-model-driven display the sibling's own picker and
    // this screen's own read-only fields already rely on, not a manual
    // editorOptions.value override.
    onCreateSeasonChanged(e) {
      this.createSeasonYear = e ? e.value : null;
      this.createSyndicateCode = null;
      this.selectedRecord = { SeasonYear: this.createSeasonYear };
      this.toolbarRecord = {};
      this.splitRows = [];

      if (this.syndicateOptionsDataSource) {
        this.syndicateOptionsDataSource.filter(
          this.createSeasonYear != null
            ? [
                ["GrowerCount", "=", 0],
                "and",
                ["SeasonYear", "=", this.createSeasonYear],
              ]
            : [["GrowerCount", "=", 0]],
        );
        this.syndicateOptionsDataSource.reload();
      }
    },

    // #76580: fires from the create-mode Syndicate picker (identityFormItems
    // above) - mirrors the sibling's own onDocketPicked. Locks the identity row
    // read-only (identityFormItems falls through to the read-only branch once
    // selectedRecord.SyndicateCode is set) and starts the grid empty, ready for
    // Add Grower.
    onCreateSyndicateChanged(e) {
      if (!e || !e.event) return;

      if (!e.value) {
        this.createSyndicateCode = null;
        // Keep SeasonYear - only the Syndicate selection was cleared.
        this.selectedRecord = { SeasonYear: this.createSeasonYear };
        this.toolbarRecord = {};
        this.splitRows = [];
        return;
      }

      const item = e.component.option("selectedItem") || {};
      this.createSyndicateCode = e.value;
      this.selectedRecord = {
        SeasonYear: this.createSeasonYear,
        SyndicateCode: item.SyndicateCode,
        SyndicateName: item.SyndicateName,
      };
      this.toolbarRecord = this.selectedRecord;
      this.splitRows = [];
      this.addNewRow();
    },
    onCreateSyndicateKeyDown(e) {
      if (forwardTabbing(e)) {
        if (this.splitRows.length > 0) {
          this.$nextTick(() => {
            const grid = this.$refs.rightGrid;
            if (grid && grid.instance) {
              const dataGrid = grid.instance;
              var cellElement = dataGrid.getCellElement(0, "GrowerCode");
              if (!cellElement) {
                return;
              }
              setTimeout(() => {
                dataGrid.focus(cellElement);
                dataGrid.editCell(0, "GrowerCode");
              }, 10);
            }
          });
        }
      }
    },

    // Resolves the fresh RecordId for a Syndicate+Season after a save - the
    // create route has no :id to reuse, and RecordId is a server-computed
    // deterministic hash, not something to reproduce client-side.
    async resolveRecordId(syndicateCode, seasonYear) {
      try {
        const res =
          await AlmondcoGrowerSyndicateSplitService.getSyndicateSplitSummaryByKey(
            syndicateCode,
            seasonYear,
          );
        const rows = (res.data && res.data.value) || [];
        return rows[0] && rows[0].RecordId;
      } catch (err) {
        return null;
      }
    },

    addNewRow() {
      this.splitRows.push({
        RowKey: this.nextRowKey++,
        GrowerCode: null,
        SplitPcnt: 0,
      });
    },
    onInitNewRow(e) {
      e.data.RowKey = this.nextRowKey++;
      e.data.SplitPcnt = 0;
    },
    onEditorPreparing(e) {
      if (e.editorOptions && e.editorOptions.onKeyDown) {
        let func = e.editorOptions.onKeyDown;
        if (this.baseView.mode != "review" && e.parentType != "searchPanel")
          e.editorOptions.onKeyDown = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
            });
          };
      }
    },
    onGrowerKeyDown(e, data) {
      if (
        backwardTabbing(e) &&
        data.rowIndex === 0 &&
        data.column.dataField === "GrowerCode"
      ) {
        e.event.preventDefault();

        this.$nextTick(() => {
          if (!this.headerFormInstance) return;
          // Create mode's header form has a SyndicateCode picker in that slot,
          // not the read-only SyndicateName textbox review/edit use (#76580).
          const field =
            this.baseView.mode === "create" ? "SyndicateCode" : "SyndicateName";
          this.headerFormInstance.getEditor(field).focus();
        });
      }
    },
    formContentReady(e) {
      if (!e) return;
      this.headerFormInstance = e.component;
      if (this.baseView.mode === "create") {
        this.$nextTick(() => {
          e.component.getEditor("SeasonYear").focus();
        });
      }
    },
    setRightGridHeight() {
      setTimeout(() => {
        const rightPanel = document.querySelector(".filter-split-container");

        if (!this.showRightPanel || !rightPanel) return;
        const formHeight = document.querySelector(
          ".selected-record-info",
        ).clientHeight;

        this.rightGridHeight =
          rightPanel.clientHeight - formHeight - 10 - 40 - 12 - 12;

        this.$nextTick(() => {
          const grid = this.$refs.rightGrid;
          if (grid && grid.instance) {
            grid.instance.updateDimensions();
          }
        });
      }, 200);
    },
    onRowRemoved() {
      this.$nextTick(() => {
        if (this.splitRows.length === 0 && this.baseView.mode === "create") {
          this.addNewRow();
        }
      });
    },
    onGridContentReady(e) {
      if (e) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.setRightGridHeight();
          }, 500);
        });
      }
    },
    onGridToolbarPreparing(e) {
      e.toolbarOptions.visible = false;
    },

    async loadGrowerOptions() {
      try {
        const res =
          await AlmondcoGrowerSyndicateSplitService.getRecipientGrowers();
        this.growerOptions = (res.data && res.data.value) || [];
      } catch (err) {
        this.growerOptions = [];
      }
    },

    growerDisplayExpr(item) {
      return item ? `${item.GrowerCode} - ${item.GrowerName}` : "";
    },
    growerDisplayText(code) {
      const opt = this.growerOptions.find((g) => g.GrowerCode === code);
      return opt ? `${opt.GrowerCode} - ${opt.GrowerName}` : code || "";
    },

    // §5: AlmondcoGrowerSyndicateSplitSummary, filtered to the selected row's
    // RecordId - resolves the identity row's Season/SyndicateName, then loads
    // the Syndicate+Season's existing split rows.
    async loadSelectedRecord(recordId) {
      // Cleared up front, not just on error - a fresh load is in flight, so the
      // mode watcher's own "already loaded, just add a row" branch (pencil
      // click on an already-open record) must not fire against stale data
      // while this one is still pending (see splitRowsLoaded below).
      this.splitRowsLoaded = false;
      this.showLoading(true);
      try {
        const res =
          await AlmondcoGrowerSyndicateSplitService.getSyndicateSplitSummary(
            recordId,
          );
        const rows = (res.data && res.data.value) || [];
        this.selectedRecord = rows[0] || {};
        this.selectedRecord.Syndicate =
          this.selectedRecord.SyndicateCode +
          " - " +
          this.selectedRecord.SyndicateName;
        this.toolbarRecord = this.selectedRecord;
        if (this.selectedRecord.SyndicateCode) {
          await this.loadSplitRows(
            this.selectedRecord.SyndicateCode,
            this.selectedRecord.SeasonYear,
          );
        } else {
          this.splitRows = [];
        }
      } catch (err) {
        this.selectedRecord = {};
        this.splitRows = [];
      } finally {
        this.showLoading(false);
        this.splitRowsLoaded = true;

        // A direct/bookmarked/refreshed load straight into Edit mode - the
        // mode watcher's own immediate handler already ran (and skipped
        // adding a row, since this load hadn't finished yet), so this is the
        // one place that actually adds it once the real rows have landed.
        if (this.baseView.mode === "edit") {
          this.addNewRow();
        }
      }
    },

    // §5: existing split rows for the selected Syndicate+Season, api.AlmondcoGrowerSplit
    // shim (reused, GrowerPoolCode = this screen's Syndicate code). Also refreshes
    // toolbarRecord from the first row here (matches the sibling grower-pool-
    // splits.vue's own loadSplitRows) - AlmondcoGrowerSyndicateSplitSummary (what
    // loadSelectedRecord itself sets toolbarRecord from) has no audit columns at
    // all; only api.AlmondcoGrowerSplit carries CreatedBy/DateCreated/ChangedBy/
    // DateChanged, which the toolbar's Audit popup reads off toolbarRecord.
    // ChangedBy/DateChanged stay blank regardless (FLAG-07, non-blocking,
    // documented - the reused legacy procedure never populates them).
    async loadSplitRows(syndicateCode, seasonYear) {
      try {
        const res = await AlmondcoGrowerSyndicateSplitService.getGrowerSplit(
          syndicateCode,
          seasonYear,
        );
        const rows = (res.data && res.data.value) || [];
        if (rows && rows.length > 0) {
          this.toolbarRecord = rows[0];
        } else {
          this.toolbarRecord = {
            CreatedBy: "",
            DateCreated: undefined,
            ChangedBy: "",
            DateChanged: undefined,
          };
        }
        this.splitRows = rows.map((r) => ({
          RowKey: this.nextRowKey++,
          GrowerCode: r.GrowerCode,
          SplitPcnt: r.SplitPcnt,
        }));
      } catch (err) {
        this.splitRows = [];
      }
    },

    // §5/§7 (BR-05/BR-05c/BR-07/BR-08): whole-grid Save gate - the only
    // enforcement point for every grid-row rule (Bug #77235). Previously
    // Split %>0/self-reference/duplicate were also (separately) wired as
    // inline field-level validationRules on the GrowerCode/SplitPcnt columns,
    // via native DevExtreme cell-edit validation - blocking, and with no
    // escape hatch once triggered (the cell wouldn't release focus, the row's
    // delete icon stopped working, and the footer kept counting the trapped
    // row anyway). All three reproduced the identical trap, confirming it was
    // the shared blocking mechanism at fault, not any one rule - so rows are
    // never blocked from being added/edited/removed in the grid now; every
    // rule is checked once, here, against the full row set, exactly like the
    // pre-existing empty-grid/Total checks below already did. The server
    // (AlmondcoGrowerSplitBusinessRulesValidator) re-checks the shared rules
    // regardless - these are the same messages, client-side, so the user
    // isn't round-tripping to the server to find out.
    validateBeforeSave() {
      if (!this.selectedRecord || !this.selectedRecord.SyndicateCode) {
        return "Select a Syndicate before saving.";
      }

      // Matches the same filter onSaveClicked's own payload build already
      // applies - a still-blank trailing row (always present in Edit mode,
      // see loadSelectedRecord/onRowRemoved) is a not-yet-started entry, not
      // an invalid one, and must not itself trip any of these checks.
      const syndicateCode = (this.selectedRecord.SyndicateCode || "")
        .trim()
        .toLowerCase();
      const filledRows = this.splitRows.filter((r) => r.GrowerCode);

      for (const row of filledRows) {
        if (!(parseFloat(row.SplitPcnt) > 0)) {
          return `Split % must be greater than 0 for Grower ${row.GrowerCode}.`;
        }
        if ((row.GrowerCode || "").trim().toLowerCase() === syndicateCode) {
          return `A Grower cannot be the Syndicate itself: ${row.GrowerCode}.`;
        }
      }

      const seenGrowerCodes = new Set();
      for (const row of filledRows) {
        const code = (row.GrowerCode || "").trim().toLowerCase();
        if (seenGrowerCodes.has(code)) {
          return `This Grower has already been entered: ${row.GrowerCode}.`;
        }
        seenGrowerCodes.add(code);
      }

      if (filledRows.length === 0) {
        return "Grower Split list does not have value(s).";
      }
      if (!this.totalIsValid) {
        return "Grower Total percentage split must equal 100%.";
      }
      return null;
    },

    // itemSaveCallBack(isValid, brokenRules) per GcFormHeaderToolbar's own save
    // button contract - isValid/brokenRules describe the toolbar's own (empty,
    // here) form, not our grid, so both are unused.
    async onSaveClicked() {
      const activeElement = document.activeElement;
      if (activeElement) {
        activeElement.blur();
      }

      await this.$nextTick();
      const blockingMessage = this.validateBeforeSave();
      if (blockingMessage) {
        // Modal, not a toast - honours the AC's "not just a Save-time toast".
        await this.$alert(blockingMessage, "Unable to Save");
        return;
      }

      this.showLoading(true);
      try {
        // AD-02/BR-10: always DepotDocket "0" plus this screen's own SuppCode
        // (the Syndicate's own code) explicitly - unlike Grower Pool Splits,
        // which omits SuppCode and lets it resolve server-side.
        const payload = {
          DepotDocket: "0",
          SeasonYear: this.selectedRecord.SeasonYear,
          SuppCode: this.selectedRecord.SyndicateCode,
          GrowerSplits: this.splitRows
            .filter((r) => r.GrowerCode)
            .map((r) => ({
              GrowerCode: r.GrowerCode,
              SplitPcnt: r.SplitPcnt,
            })),
        };

        await AlmondcoGrowerSyndicateSplitService.saveGrowerSplit(payload);

        // #76580: the create route has no :id to reuse (a Season+Syndicate
        // wasn't picked via a row click), and RecordId is a server-computed
        // deterministic hash, not something to reproduce client-side - resolve
        // it fresh regardless of mode rather than assume baseView.id is valid.
        const recordId = await this.resolveRecordId(
          this.selectedRecord.SyndicateCode,
          this.selectedRecord.SeasonYear,
        );

        this.setIgnoreRouteChangeConfirmation();
        await this.$router.push({
          path: `${this.leftGridConfig.pageRoute}/review/${
            recordId || this.baseView.id
          }`,
        });
      } catch (err) {
        // ApiService interceptor rejects with error.response (the response
        // object), so err.data is the body - matching the sibling's own error
        // handling. Save_AlmondcoGrowerSplitController returns a BadRequest
        // array of ValidationResult-shaped objects on business-rule failure.
        var data =
          (err && err.data) || (err && err.response && err.response.data);
        var message =
          "An error occurred while saving the Grower Syndicate Split.";
        if (Array.isArray(data) && data.length > 0) {
          message = data
            .map(function (e) {
              return e.ErrorMessage || e;
            })
            .join(" ");
        } else if (typeof data === "string" && data.length > 0) {
          message = data;
        }
        await this.$alert(message, "Unable to Save");
      } finally {
        this.showLoading(false);
        this.setRightGridHeight();

        if (this.baseView.id) {
          this.loadSelectedRecord(this.baseView.id);
        }
      }
    },
  },
};
</script>

<style lang="scss">
.grower-syndicate-splits {
  .split-grid-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    font-size: 12px;
    color: #667085;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    padding-right: 5px !important;
  }

  .total-value {
    font-weight: 600;
    // margin-left: 4px;
  }

  /* Matches the sibling grower-pool-splits.vue's own good/bad colours for this
     exact panel shape. */
  .total-value.good {
    color: #1f7a3d;
  }
  .total-value.bad {
    color: #a3222f;
  }

  .selected-record-info {
    padding: 12px 0px 12px 0px;

    .dx-form {
      background-color: white;
      padding: 12px;
    }
  }

  .split-view-right-grid {
    padding: unset !important;

    .dx-datagrid-header-panel {
      height: 0px !important;
    }
  }
}
</style>
